import {
  ACCESSIBILITY_ARGS,
  ANCHORED_ARGS,
  Arg,
  COMPONENT_STATE_ARGS,
  CONTAINER_ARGS,
  INPUT_FORM_ARGS,
  INPUT_MENU_ARGS,
  MENU_PLACEMENT_ARG,
  Metadata,
  SIZING_ARGS,
  pick,
} from '../../../utils/spds/metadata';
import { HIDDEN_SELECTOR } from '../../../utils/keyboard-nav-support';
import { MENU_MAX_HEIGHT } from '../../../utils/spds/constants/menu';
import { ROLE_LISTBOX } from '../../../utils/spds/constants/aria';
import { applyBoldMatchingText, stripBolding } from '../../../modifiers/bold-matching-text';
import { filterMenuItemsByText } from '../../../utils/spds/menu-filter';
import { fn } from '@ember/helper';
import { modifier } from 'ember-modifier';
import { or } from 'ember-truth-helpers';
import { task } from 'ember-concurrency';
import { testAwareTimeout } from '../../../utils/waiters';
import { tracked } from '@glimmer/tracking';
import Menu from '../core/menu/base.gjs';
import MenuSearch from './search.gjs';
import Super from '../core/base.gjs';

const SEARCH_DEBOUNCE_MS = 200;

const METADATA = Metadata.define({
  description: `
Search-enabled menu. Extends \`Spds::Core::Menu::Base\` with an in-menu search field that filters the
menu items by text as the user types.

### In-menu search

When a menu renders at least \`@searchThreshold\` items (15 by default), a search field is automatically added to the top of the menu that
filters the items by text as the user types. This can be disabled per-menu by passing \`@searchable={{false}}\`, or forced on regardless of the
item count with \`@searchable={{true}}\`. When the active filter matches no items, a no-results message (customizable via \`@noResultsText\`) is shown.
  `,
  extends: [
    pick(ACCESSIBILITY_ARGS, [{ name: 'role', default: ROLE_LISTBOX }, 'ariaLabelledBy']),
    pick(ANCHORED_ARGS, ['anchorElement', 'anchorOffset', MENU_PLACEMENT_ARG]),
    pick(COMPONENT_STATE_ARGS, [{ name: 'animated', default: true }, 'open']),
    pick(CONTAINER_ARGS, ['fitContent', { name: 'column', default: true }]),
    pick(INPUT_MENU_ARGS, [
      'multiselect',
      'noItemsDisplayed',
      'noResultsText',
      'searchable',
      'searchPlaceholder',
      'searchThreshold',
    ]),
    pick(INPUT_FORM_ARGS, [
      { name: 'charLimit', rename: 'searchCharLimit' },
      { name: 'hideCharacterCounter', rename: 'searchHideCharacterCounter' },
    ]),
    pick(SIZING_ARGS, [
      { name: 'maxHeight', default: MENU_MAX_HEIGHT },
      'maxWidth',
      'minWidth',
      'width',
    ]),
  ],
  meta: {
    autoFilter: Arg.boolean({
      default: true,
      description:
        'When true, the menu applies its built-in text filter to the items. Set false to let a parent own filtering (e.g. custom/async search).',
    }),
    items: Arg.array({
      description: 'Navigable menu item elements that the menu filters over',
    }),
    searchValue: Arg.string({
      description:
        'External filter term supplied by a parent (search input). When provided, the built-in search field is not rendered and this term drives filtering.',
    }),
  },
  name: 'Filterable Menu',
});

export default class SpdsMenuFilterable extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);

  @tracked searchComponent;
  @tracked filterValue = '';
  @tracked visibleCount = 0;

  get animated() {
    return this.argOrDefault('animated');
  }

  get autoFilterEnabled() {
    return this.argOrDefault('autoFilter');
  }

  get column() {
    return this.argOrDefault('column');
  }

  get externalFilter() {
    return this.args.searchValue !== undefined;
  }

  get filterActive() {
    return this.autoFilterEnabled && this.filterTerm.length > 0;
  }

  get filterTerm() {
    return this.args.searchValue ?? this.filterValue;
  }

  get items() {
    return this.args.items ?? [];
  }

  get itemCount() {
    return this.items?.length;
  }

  get multiselect() {
    return this.argOrDefault('multiselect');
  }

  get role() {
    return this.argOrDefault('role');
  }

  get searchableIsSet() {
    let { searchable } = this.args;

    return searchable === Boolean(searchable);
  }

  get searchId() {
    return this.composeSubcomponentId('search');
  }

  get searchInputElement() {
    return this.searchComponent?.inputElement;
  }

  get searchThreshold() {
    return this.argOrDefault('searchThreshold');
  }

  get searchEnabled() {
    if (this.searchableIsSet) return this.args.searchable;

    return this.itemCount >= this.searchThreshold;
  }

  get showNoResults() {
    if (this.args.noItemsDisplayed) return true;

    return this.filterActive && this.visibleCount === 0;
  }

  // The task, not the input, owns the debounce: `filterValue` updates
  // immediately as the user types (keeping the field responsive and the caret
  // stable) while this restartable task waits for typing to settle before
  // applying the filter. Each keystroke restarts it, cancelling the in-flight
  // run.
  @(task(function* () {
    yield testAwareTimeout(SEARCH_DEBOUNCE_MS);
    this.runFilter();
  }).restartable())
  filterTask;

  syncSearchToOpenState = modifier((element, [open]) => {
    if (!open) {
      this.resetSearch();
      return;
    }

    if (this.searchEnabled) this.searchInputElement?.focus();
  });

  // Non-reactive record of the value the filter last saw. Used only to tell a
  // value change (typing) apart from an item-set change within `applyFilter`.
  #filteredValue = '';

  // The items this filter has hidden. Tracked so a reset reveals only what the
  // filter itself hid, never a consumer's own declarative `hidden` state.
  #filterHidden = new Set();

  // Single entry point for (re)applying the filter, keyed on both the term and
  // the item set. The filter hides items imperatively (toggling `hidden`), so it
  // is not re-derived on render:
  // - Internal in-menu typing debounces via `filterTask` so we don't filter on
  //   every keystroke.
  // - An external term (search input) is applied immediately—the parent already
  //   controls the typing cadence, so a second debounce would only add lag.
  // - When only the item set changed while a filter is active (e.g. select-all
  //   rebuilding the checkbox group's registered items, which wipes the `hidden`
  //   state on re-render), re-apply synchronously so the filtered rows never
  //   flash back to the full list.
  //
  // When idle (no active filter and no external term) we deliberately do
  // nothing: `runFilter` toggles the shared `hidden` class, so running it with an
  // empty value would clobber any externally managed `hidden` state (e.g. a
  // consumer hiding already-selected options). When `@autoFilter` is false the
  // parent owns filtering entirely, so we stay out of it.
  applyFilter = modifier((element, [value, items]) => {
    if (!this.autoFilterEnabled) return;

    let valueChanged = value !== this.#filteredValue;
    this.#filteredValue = value;

    if (valueChanged && !this.externalFilter) {
      this.filterTask.perform();
      return;
    }

    if (this.externalFilter || this.filterActive) {
      this.filterTask.cancelAll();
      this.runFilter(value, items);
    }
  });

  runFilter(value = this.filterTerm, items = this.items) {
    let { multiselect } = this;
    let matches = filterMenuItemsByText(items, value, { multiselect });
    let excluded = items.filter(el => !matches.includes(el));

    matches.forEach(el => {
      let display = multiselect ? el.parentElement : el;
      if (!display) return;

      if (value) {
        applyBoldMatchingText(display, value);
      } else {
        stripBolding(display.querySelector('span') ?? display);
      }
    });

    excluded.forEach(el => {
      let display = multiselect ? el.parentElement : el;
      if (!display) return;
      stripBolding(display.querySelector('span') ?? display);
    });

    this.#filterHidden = new Set(excluded);
    this.visibleCount = matches.length;
  }

  resetSearch = () => {
    this.filterTask.cancelAll();
    this.#filteredValue = '';
    this.filterValue = '';
    this.revealFilteredItems();
  };

  // Reveal only the items this filter hid (tracked in `#filterHidden`) so a
  // consumer's own declarative `hidden` state is left intact, and strip any
  // bolding the filter applied.
  revealFilteredItems() {
    let { multiselect } = this;

    this.#filterHidden.forEach(el => {
      let display = multiselect ? el.parentElement : el;
      display?.classList.remove(HIDDEN_SELECTOR);
    });
    this.#filterHidden = new Set();

    this.items.forEach(el => {
      let display = multiselect ? el.parentElement : el;
      if (display) stripBolding(display.querySelector('span') ?? display);
    });

    this.visibleCount = this.items.length;
  }

  clearSearch = () => {
    this.resetSearch();
    this.searchInputElement?.focus();
  };

  onSearchInput = evt => {
    this.filterValue = evt?.target?.value ?? '';
  };

  <template>
    <Menu
      @anchorElement={{@anchorElement}}
      @anchorOffset={{@anchorOffset}}
      @animated={{this.animated}}
      @fitContent={{@fitContent}}
      @id={{this.id}}
      @maxHeight={{@maxHeight}}
      @maxWidth={{@maxWidth}}
      @minWidth={{@minWidth}}
      @open={{@open}}
      @placement={{@placement}}
      @registerComponent={{fn this.registerComponent "baseComponent"}}
      @unregisterComponent={{fn this.unregisterComponent "baseComponent"}}
      @width={{@width}}
      aria-expanded={{@open}}
      class={{this.classes}}
      data-test-spds-menu-filterable
      {{this.syncSearchToOpenState @open}}
      {{this.applyFilter this.filterTerm this.items}}
      {{this.validateArguments}}
      ...attributes
      as |menu|
    >
      {{#if (or this.searchEnabled (has-block "header"))}}
        <menu.Header class="spds-menu-filterable-header">
          {{#if this.searchEnabled}}
            <MenuSearch
              @ariaControls={{menu.listId}}
              @charLimit={{@searchCharLimit}}
              @flex="1"
              @hideCharacterCounter={{@searchHideCharacterCounter}}
              @id={{this.searchId}}
              @onClear={{this.clearSearch}}
              @onInput={{this.onSearchInput}}
              @placeholder={{@searchPlaceholder}}
              @registerComponent={{fn this.registerComponent "searchComponent"}}
              @unregisterComponent={{fn this.unregisterComponent "searchComponent"}}
              @value={{this.filterValue}}
              data-test-spds-menu-filterable-search
            />
          {{/if}}
          {{yield to="header"}}
        </menu.Header>
      {{/if}}
      <menu.Body
        @ariaLabelledBy={{@ariaLabelledBy}}
        @column={{this.column}}
        @noResultsText={{@noResultsText}}
        @registerListComponent={{fn this.registerComponent "listComponent"}}
        @role={{this.role}}
        @showNoResults={{this.showNoResults}}
        @unregisterListComponent={{fn this.unregisterComponent "listComponent"}}
      >
        {{#if (has-block "body")}}
          {{yield this.filterTerm to="body"}}
        {{else}}
          {{yield this.filterTerm}}
        {{/if}}
      </menu.Body>

      {{#if (has-block "footer")}}
        <menu.Footer @alignItems="center" @column={{false}}>
          {{yield to="footer"}}
        </menu.Footer>
      {{/if}}
    </Menu>
  </template>
}

export const SPDS_MENU_FILTERABLE_METADATA = SpdsMenuFilterable.metadata;
